import { supabase } from "./supabase";

/**
 * Wise Account Requirements Service
 * Handles fetching dynamic form requirements from Wise API
 * Based on: https://docs.wise.com/api-docs/api-reference/account-requirements
 */

const wiseAccountRequirementsService = {
  /**
   * Get account requirements for a specific currency pair
   * @param {string} sourceCurrency - Source currency (e.g., "USD")
   * @param {string} targetCurrency - Target currency (e.g., "EUR")
   * @param {number} sourceAmount - Amount to transfer (default: 100)
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getAccountRequirements(sourceCurrency, targetCurrency, sourceAmount = 100) {
    try {
      console.log(`🔍 Getting account requirements for ${sourceCurrency} → ${targetCurrency}`);

      const { data, error } = await supabase.functions.invoke("wise-recipients", {
        body: {
          action: "get-requirements",
          sourceCurrency: sourceCurrency,
          targetCurrency: targetCurrency,
          sourceAmount: sourceAmount,
        },
      });

      if (error) {
        console.error("❌ Error getting account requirements:", error);
        throw error;
      }

      if (!data.success) {
        console.error("❌ Account requirements request failed:", data.error);
        return { success: false, error: data.error };
      }

      console.log("✅ Account requirements retrieved:", data.requirements);
      return { 
        success: true, 
        data: {
          requirements: data.requirements,
          quote: data.quote
        }
      };
    } catch (err) {
      console.error("❌ Error in getAccountRequirements:", err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Parse account requirements into a form schema
   * @param {Array} requirements - Requirements array from Wise API
   * @returns {Object} Parsed form schema with fields and validation
   */
  parseRequirementsToFormSchema(requirements) {
    if (!requirements || !Array.isArray(requirements)) {
      return { fields: [], validation: {} };
    }

    const fields = [];
    const validation = {};

    requirements.forEach((requirement) => {
      if (requirement.type === "recipient") {
        requirement.fields?.forEach((field) => {
          const fieldConfig = {
            name: field.key,
            label: field.name,
            type: this.mapWiseFieldType(field.type),
            required: field.required,
            placeholder: field.example || "",
            options: field.valuesAllowed?.map(option => ({
              value: option.key,
              label: option.name
            })) || null,
            validation: field.validationRegexp || null,
            minLength: field.minLength || null,
            maxLength: field.maxLength || null,
            group: field.group || "default"
          };

          fields.push(fieldConfig);

          // Build validation rules
          if (field.required) {
            validation[field.key] = validation[field.key] || [];
            validation[field.key].push("required");
          }

          if (field.validationRegexp) {
            validation[field.key] = validation[field.key] || [];
            validation[field.key].push(`pattern:${field.validationRegexp}`);
          }

          if (field.minLength) {
            validation[field.key] = validation[field.key] || [];
            validation[field.key].push(`minLength:${field.minLength}`);
          }

          if (field.maxLength) {
            validation[field.key] = validation[field.key] || [];
            validation[field.key].push(`maxLength:${field.maxLength}`);
          }
        });
      }
    });

    return { fields, validation };
  },

  /**
   * Map Wise field types to HTML input types
   * @param {string} wiseType - Wise field type
   * @returns {string} HTML input type
   */
  mapWiseFieldType(wiseType) {
    const typeMap = {
      "text": "text",
      "select": "select",
      "radio": "radio",
      "date": "date",
      "email": "email",
      "phone": "tel",
      "number": "number",
      "textarea": "textarea"
    };

    return typeMap[wiseType] || "text";
  },

  /**
   * Group fields by their logical sections
   * @param {Array} fields - Parsed fields array
   * @returns {Object} Fields grouped by section
   */
  groupFieldsBySection(fields) {
    const groups = {
      personal: [],
      address: [],
      banking: [],
      other: []
    };

    fields.forEach(field => {
      const key = field.name.toLowerCase();
      
      if (key.includes("name") || key.includes("email") || key.includes("phone")) {
        groups.personal.push(field);
      } else if (key.includes("address") || key.includes("city") || key.includes("state") || 
                 key.includes("country") || key.includes("postal") || key.includes("zip")) {
        groups.address.push(field);
      } else if (key.includes("account") || key.includes("routing") || key.includes("iban") || 
                 key.includes("swift") || key.includes("sort") || key.includes("bsb")) {
        groups.banking.push(field);
      } else {
        groups.other.push(field);
      }
    });

    return groups;
  }
};

export { wiseAccountRequirementsService };